/**
 * Mancala (Kalah) Game Engine
 *
 * Core game logic: board setup, move validation, seed sowing,
 * capture rules, extra turns, game-over detection, scoring, serialization.
 *
 * Board layout:
 *   Player 2's store [5][4][3][2][1][0]  ← Player 2's pits (top row, displayed right-to-left)
 *                     [0][1][2][3][4][5]  → Player 1's pits (bottom row, left-to-right)
 *                                          Player 1's store
 *
 * Sowing is counter-clockwise:
 *   Player 1: right along bottom → into P1 store → left along top → skip P2 store → ...
 *   Player 2: right along top → into P2 store → left along bottom → skip P1 store → ...
 */

import {
  PlayerSide,
  MancalaBoardState,
  MancalaMove,
  MancalaMoveResult,
  MancalaGameState,
  MancalaGameSettings,
  PITS_PER_SIDE,
  SEEDS_PER_PIT,
  TOTAL_SEEDS,
} from './types';

export class MancalaEngine {
  private state: MancalaGameState;

  constructor() {
    this.state = this.createEmptyState();
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  /**
   * Initialize a new game with the given settings.
   * Each of the 12 pits gets SEEDS_PER_PIT (4) seeds. Stores start empty.
   */
  initializeGame(_settings?: MancalaGameSettings): void {
    const pits: number[][] = [
      Array(PITS_PER_SIDE).fill(SEEDS_PER_PIT),
      Array(PITS_PER_SIDE).fill(SEEDS_PER_PIT),
    ];
    const stores: number[] = [0, 0];

    this.state = {
      board: { pits, stores },
      currentPlayer: 'player1',
      moveHistory: [],
      gameStatus: 'active',
      score: { player1: 0, player2: 0 },
      gameId: `mancala-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      startedAt: Date.now(),
    };
  }

  // =========================================================================
  // Move Validation
  // =========================================================================

  /**
   * Get all valid pit indices for the given player.
   * A pit is valid if it belongs to the player and has seeds.
   */
  getValidMoves(player?: PlayerSide): number[] {
    const p = player || this.state.currentPlayer;
    const sideIndex = p === 'player1' ? 0 : 1;
    const validPits: number[] = [];

    for (let i = 0; i < PITS_PER_SIDE; i++) {
      if (this.state.board.pits[sideIndex][i] > 0) {
        validPits.push(i);
      }
    }

    return validPits;
  }

  /**
   * Check if a specific pit is a valid move for the given player.
   */
  isValidMove(player: PlayerSide, pitIndex: number): boolean {
    if (pitIndex < 0 || pitIndex >= PITS_PER_SIDE) return false;
    const sideIndex = player === 'player1' ? 0 : 1;
    return this.state.board.pits[sideIndex][pitIndex] > 0;
  }

  // =========================================================================
  // Move Execution
  // =========================================================================

  /**
   * Execute a move: pick up all seeds from the chosen pit and sow them
   * counter-clockwise, one per pit. Handle extra turns and captures.
   */
  executeMove(player: PlayerSide, pitIndex: number): MancalaMoveResult {
    if (this.state.gameStatus !== 'active') {
      throw new Error('Game is not active');
    }

    if (player !== this.state.currentPlayer) {
      throw new Error(`Not ${player}'s turn`);
    }

    if (!this.isValidMove(player, pitIndex)) {
      throw new Error(`Invalid move: pit ${pitIndex} is empty or out of range`);
    }

    const sideIndex = player === 'player1' ? 0 : 1;
    const opponentSideIndex = player === 'player1' ? 1 : 0;

    // Pick up all seeds from the chosen pit
    let seeds = this.state.board.pits[sideIndex][pitIndex];
    this.state.board.pits[sideIndex][pitIndex] = 0;

    // Sow seeds counter-clockwise
    // We model the board as a circular track:
    // Player 1's pits: indices 0-5
    // Player 1's store: index 6
    // Player 2's pits: indices 7-12
    // Player 2's store: index 13
    // Total: 14 positions, but we skip the opponent's store

    let currentPos = this.toLinearPosition(sideIndex, pitIndex);
    let lastLandingSide = -1;
    let lastLandingPit = -1;
    let landedInStore = false;

    while (seeds > 0) {
      currentPos = (currentPos + 1) % 14;

      // Skip opponent's store
      if (player === 'player1' && currentPos === 13) continue;
      if (player === 'player2' && currentPos === 6) continue;

      // Drop a seed
      const { side, pit, isStore } = this.fromLinearPosition(currentPos);

      if (isStore) {
        this.state.board.stores[side] += 1;
        lastLandingSide = side;
        lastLandingPit = -1;
        landedInStore = true;
      } else {
        this.state.board.pits[side][pit] += 1;
        lastLandingSide = side;
        lastLandingPit = pit;
        landedInStore = false;
      }

      seeds--;
    }

    // Check for extra turn: last seed landed in player's own store
    const extraTurn = landedInStore && lastLandingSide === sideIndex;

    // Check for capture: last seed landed in an empty pit on player's own side
    let captured = false;
    let capturedSeeds = 0;

    if (
      !landedInStore &&
      lastLandingSide === sideIndex &&
      lastLandingPit >= 0 &&
      this.state.board.pits[sideIndex][lastLandingPit] === 1 // Was empty before this seed
    ) {
      // Check if opposite pit has seeds
      const oppositePit = PITS_PER_SIDE - 1 - lastLandingPit;
      const oppositeSeeds = this.state.board.pits[opponentSideIndex][oppositePit];

      if (oppositeSeeds > 0) {
        // Capture: move the landing seed + opposite seeds to player's store
        captured = true;
        capturedSeeds = 1 + oppositeSeeds;
        this.state.board.stores[sideIndex] += capturedSeeds;
        this.state.board.pits[sideIndex][lastLandingPit] = 0;
        this.state.board.pits[opponentSideIndex][oppositePit] = 0;
      }
    }

    // Record the move
    const move: MancalaMove = {
      pitIndex,
      player,
      timestamp: Date.now(),
    };
    this.state.moveHistory.push(move);

    // Update scores
    this.state.score = {
      player1: this.state.board.stores[0],
      player2: this.state.board.stores[1],
    };

    const result: MancalaMoveResult = {
      extraTurn,
      captured,
      capturedSeeds,
      lastLandingPit,
      landedInStore,
      board: this.cloneBoard(),
      move,
    };

    this.state.lastMoveResult = result;

    return result;
  }

  // =========================================================================
  // Turn Management
  // =========================================================================

  /**
   * Advance to the next player's turn, unless the current player earned
   * an extra turn.
   */
  advanceTurn(): void {
    if (this.state.gameStatus !== 'active') return;

    // Check if game is over first
    if (this.checkGameOver()) {
      this.collectRemaining();
      this.endGame();
      return;
    }

    // If the last move result was an extra turn, don't switch
    if (this.state.lastMoveResult?.extraTurn) {
      // Player keeps their turn — but check if they have valid moves
      const validMoves = this.getValidMoves(this.state.currentPlayer);
      if (validMoves.length === 0) {
        // No valid moves even with extra turn — game over
        this.collectRemaining();
        this.endGame();
      }
      return;
    }

    // Switch to opponent
    this.state.currentPlayer =
      this.state.currentPlayer === 'player1' ? 'player2' : 'player1';

    // Check if the new current player has valid moves
    const validMoves = this.getValidMoves(this.state.currentPlayer);
    if (validMoves.length === 0) {
      // No valid moves — game over
      this.collectRemaining();
      this.endGame();
    }
  }

  // =========================================================================
  // Game Over
  // =========================================================================

  /**
   * Check if the game is over: one side has all pits empty.
   */
  isGameOver(): boolean {
    return this.state.gameStatus === 'completed';
  }

  /**
   * Check if either side has all pits empty (game should end).
   */
  private checkGameOver(): boolean {
    const p1Empty = this.state.board.pits[0].every(s => s === 0);
    const p2Empty = this.state.board.pits[1].every(s => s === 0);
    return p1Empty || p2Empty;
  }

  /**
   * Collect remaining seeds: all seeds left on a side go to that side's store.
   */
  collectRemaining(): void {
    for (let side = 0; side < 2; side++) {
      let remaining = 0;
      for (let i = 0; i < PITS_PER_SIDE; i++) {
        remaining += this.state.board.pits[side][i];
        this.state.board.pits[side][i] = 0;
      }
      this.state.board.stores[side] += remaining;
    }

    // Update scores
    this.state.score = {
      player1: this.state.board.stores[0],
      player2: this.state.board.stores[1],
    };
  }

  /**
   * End the game and determine the winner.
   */
  private endGame(): void {
    this.state.gameStatus = 'completed';
    const { player1, player2 } = this.state.score;

    if (player1 > player2) {
      this.state.winner = 'player1';
    } else if (player2 > player1) {
      this.state.winner = 'player2';
    } else {
      this.state.winner = 'draw';
    }
  }

  // =========================================================================
  // State Queries
  // =========================================================================

  getScore(): { player1: number; player2: number } {
    return { ...this.state.score };
  }

  getWinner(): PlayerSide | 'draw' | undefined {
    return this.state.winner;
  }

  getBoard(): MancalaBoardState {
    return this.cloneBoard();
  }

  getCurrentPlayer(): PlayerSide {
    return this.state.currentPlayer;
  }

  getGameState(): MancalaGameState {
    return {
      ...this.state,
      board: this.cloneBoard(),
      moveHistory: [...this.state.moveHistory],
      score: { ...this.state.score },
      lastMoveResult: this.state.lastMoveResult
        ? { ...this.state.lastMoveResult, board: this.cloneBoard() }
        : undefined,
    };
  }

  getMoveHistory(): MancalaMove[] {
    return [...this.state.moveHistory];
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  /** Serialize the complete game state to JSON string */
  serialize(): string {
    return JSON.stringify(this.state);
  }

  /** Deserialize a JSON string to game state */
  deserialize(json: string): MancalaGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);

      if (!parsed.board || !parsed.board.pits || !parsed.board.stores) {
        return { error: 'Invalid state: missing board data' };
      }
      if (
        !Array.isArray(parsed.board.pits) ||
        parsed.board.pits.length !== 2 ||
        parsed.board.pits[0].length !== PITS_PER_SIDE ||
        parsed.board.pits[1].length !== PITS_PER_SIDE
      ) {
        return { error: 'Invalid state: pits must be 2×6' };
      }
      if (
        !Array.isArray(parsed.board.stores) ||
        parsed.board.stores.length !== 2
      ) {
        return { error: 'Invalid state: stores must have 2 entries' };
      }
      if (!['player1', 'player2'].includes(parsed.currentPlayer)) {
        return { error: 'Invalid state: invalid currentPlayer' };
      }
      if (!['active', 'completed'].includes(parsed.gameStatus)) {
        return { error: 'Invalid state: invalid gameStatus' };
      }

      return parsed as MancalaGameState;
    } catch (e) {
      return {
        error: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}`,
      };
    }
  }

  /** Set the game state directly (for state restoration) */
  setGameState(state: MancalaGameState): void {
    this.state = {
      ...state,
      board: {
        pits: state.board.pits.map(row => [...row]),
        stores: [...state.board.stores],
      },
      moveHistory: [...state.moveHistory],
      score: { ...state.score },
    };
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  private createEmptyState(): MancalaGameState {
    return {
      board: {
        pits: [
          Array(PITS_PER_SIDE).fill(0),
          Array(PITS_PER_SIDE).fill(0),
        ],
        stores: [0, 0],
      },
      currentPlayer: 'player1',
      moveHistory: [],
      gameStatus: 'active',
      score: { player1: 0, player2: 0 },
      gameId: '',
      startedAt: 0,
    };
  }

  private cloneBoard(): MancalaBoardState {
    return {
      pits: this.state.board.pits.map(row => [...row]),
      stores: [...this.state.board.stores],
    };
  }

  /**
   * Convert a (side, pit) position to a linear position (0-13).
   * Linear layout:
   *   0-5: Player 1's pits
   *   6: Player 1's store
   *   7-12: Player 2's pits
   *   13: Player 2's store
   */
  private toLinearPosition(side: number, pit: number): number {
    if (side === 0) return pit;
    return 7 + pit;
  }

  /**
   * Convert a linear position (0-13) back to (side, pit, isStore).
   */
  private fromLinearPosition(
    pos: number
  ): { side: number; pit: number; isStore: boolean } {
    if (pos >= 0 && pos <= 5) {
      return { side: 0, pit: pos, isStore: false };
    }
    if (pos === 6) {
      return { side: 0, pit: -1, isStore: true };
    }
    if (pos >= 7 && pos <= 12) {
      return { side: 1, pit: pos - 7, isStore: false };
    }
    // pos === 13
    return { side: 1, pit: -1, isStore: true };
  }
}
